import { useEffect, useMemo } from 'react';
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  MenuItem,
  Stack,
  TextField,
} from '@mui/material';
import { useForm, Controller } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import * as yup from 'yup';

export const GRADE_OPTIONS = [
  ...Array.from({ length: 12 }, (_, idx) => ({ value: String(idx + 1), label: `${idx + 1} клас` })),
  { value: 'college', label: 'Коледж' },
  { value: 'university', label: 'Університет' },
];

export const SUBJECT_OPTIONS = [
  { value: 'mathematics', label: 'Математика' },
  { value: 'ukrainian', label: 'Українська мова' },
  { value: 'chemistry', label: 'Хімія' },
  { value: 'biology', label: 'Біологія' },
  { value: 'physics', label: 'Фізика' },
  { value: 'history', label: 'Історія' },
];

const schema = yup.object({
  name: yup.string().required("Ім'я обовʼязкове"),
  grade: yup.string().required('Оберіть клас/рівень навчання'),
  subjects: yup
    .string()
    .oneOf(SUBJECT_OPTIONS.map((option) => option.value))
    .required('Оберіть предмет'),
  parentContact: yup.string().nullable(),
  tutorId: yup.number().required('Виберіть репетитора'),
  parentId: yup.number().required('Виберіть батьків'),
});

const normalizeDefaults = (values = {}) => ({
  name: values.name || '',
  grade: values.grade || '',
  subjects: values.subjects || '',
  parentContact: values.parentContact || '',
  tutorId: values.tutorId ? Number(values.tutorId) : '',
  parentId: values.parentId ? Number(values.parentId) : '',
});

const STORAGE_KEY = 'student_form_defaults';

const StudentFormDialog = ({ open, onClose, onSubmit, initialValues, tutors, parents, title }) => {
  const {
    handleSubmit,
    reset,
    control,
    formState: { errors, isSubmitting },
  } = useForm({
    resolver: yupResolver(schema),
    defaultValues: normalizeDefaults(initialValues),
  });

  useEffect(() => {
    if (open) {
      if (!initialValues?.id) {
        const saved = localStorage.getItem(STORAGE_KEY);
        if (saved) {
          try {
            const parsed = normalizeDefaults(JSON.parse(saved));
            reset({ ...normalizeDefaults(initialValues), ...parsed });
            return;
          } catch {
            // ignore
          }
        }
      }
      reset(normalizeDefaults(initialValues));
    }
  }, [initialValues, open, reset]);

  const computedGradeOptions = useMemo(() => {
    if (initialValues?.grade && !GRADE_OPTIONS.find((option) => option.value === initialValues.grade)) {
      return [...GRADE_OPTIONS, { value: initialValues.grade, label: initialValues.grade }];
    }
    return GRADE_OPTIONS;
  }, [initialValues?.grade]);

  const computedSubjectOptions = useMemo(() => {
    if (initialValues?.subjects && !SUBJECT_OPTIONS.find((option) => option.value === initialValues.subjects)) {
      return [...SUBJECT_OPTIONS, { value: initialValues.subjects, label: initialValues.subjects }];
    }
    return SUBJECT_OPTIONS;
  }, [initialValues?.subjects]);

  const handleFormSubmit = handleSubmit(async (values) => {
    await onSubmit({ ...values, tutorId: Number(values.tutorId), parentId: Number(values.parentId) });
    const defaults = normalizeDefaults({
      grade: values.grade,
      subjects: values.subjects,
      tutorId: values.tutorId,
      parentId: values.parentId,
      parentContact: values.parentContact,
    });
    localStorage.setItem(STORAGE_KEY, JSON.stringify(defaults));
    onClose();
  });

  return (
    <Dialog open={open} onClose={onClose} maxWidth="sm" fullWidth>
      <DialogTitle>{title}</DialogTitle>
      <DialogContent>
        <Stack spacing={2} mt={1}>
          <Controller
            name="name"
            control={control}
            render={({ field }) => (
              <TextField
                label="Ім'я"
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.name)}
                helperText={errors.name?.message}
                fullWidth
              />
            )}
          />
          <Controller
            name="grade"
            control={control}
            render={({ field }) => (
              <TextField
                label="Клас / рівень"
                select
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.grade)}
                helperText={errors.grade?.message}
                fullWidth
              >
                {computedGradeOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="subjects"
            control={control}
            render={({ field }) => (
              <TextField
                label="Предмет"
                select
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.subjects)}
                helperText={errors.subjects?.message}
                fullWidth
              >
                {computedSubjectOptions.map((option) => (
                  <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="parentContact"
            control={control}
            render={({ field }) => (
              <TextField
                label="Контакти батьків"
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.parentContact)}
                helperText={errors.parentContact?.message}
                fullWidth
              />
            )}
          />
          <Controller
            name="tutorId"
            control={control}
            render={({ field }) => (
              <TextField
                label="Репетитор"
                select
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.tutorId)}
                helperText={errors.tutorId?.message}
                fullWidth
              >
                {tutors.map((tutor) => (
                  <MenuItem key={tutor.id} value={tutor.id}>{tutor.name}</MenuItem>
                ))}
              </TextField>
            )}
          />
          <Controller
            name="parentId"
            control={control}
            render={({ field }) => (
              <TextField
                label="Батьки"
                select
                {...field}
                value={field.value ?? ''}
                error={Boolean(errors.parentId)}
                helperText={errors.parentId?.message}
                fullWidth
              >
                {parents.map((parent) => (
                  <MenuItem key={parent.id} value={parent.id}>{parent.name}</MenuItem>
                ))}
              </TextField>
            )}
          />
        </Stack>
      </DialogContent>
      <DialogActions>
        <Button onClick={onClose}>Скасувати</Button>
        <Button variant="contained" onClick={handleFormSubmit} disabled={isSubmitting}>
          Зберегти
        </Button>
      </DialogActions>
    </Dialog>
  );
};

export default StudentFormDialog;
